import React, { useState } from 'react';
import {
  Box, Paper, Typography, Button, Stack, CircularProgress,
  Alert, Tooltip,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import CloudIcon from '@mui/icons-material/Cloud';
import DatasetIcon from '@mui/icons-material/Dataset';
import RefreshIcon from '@mui/icons-material/Refresh';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import FaceIcon from '@mui/icons-material/Face';
import SearchIcon from '@mui/icons-material/Search';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ImageIcon from '@mui/icons-material/Image';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  const val = bytes / Math.pow(k, i);
  return `${i === 0 ? val : val.toFixed(val < 10 ? 2 : 1)} ${sizes[i]}`;
}

function pct(part, total) {
  if (!total) return 0;
  return Math.max((part / total) * 100, part > 0 ? 0.5 : 0);
}

// ── Visual config ─────────────────────────────────────────────────────────────

const S3_CATS = [
  { key: 'photos',        label: 'Event Photos',  color: '#1565C0', Icon: PhotoLibraryIcon },
  { key: 'face_memory',   label: 'Face Memory',   color: '#6A1B9A', Icon: FaceIcon },
  { key: 'search_indexes',label: 'Search Indexes',color: '#00838F', Icon: SearchIcon },
  { key: 'profile',       label: 'Profile Photo', color: '#BF360C', Icon: AccountCircleIcon },
];

const DB_CATS = [
  { key: 'face_data',   label: 'Face Recognition Data', color: '#283593', Icon: PsychologyIcon },
  { key: 'photo_data',  label: 'Photo Records',         color: '#1B5E20', Icon: ImageIcon },
  { key: 'account_data',label: 'Account & Billing',     color: '#4E342E', Icon: ManageAccountsIcon },
];

// ── Sub-components ────────────────────────────────────────────────────────────

function StackedBar({ categories, total, catConfig }) {
  if (!total) {
    return (
      <Box sx={{ height: 18, borderRadius: 2, bgcolor: 'grey.200', width: '100%' }} />
    );
  }
  return (
    <Box sx={{ display: 'flex', width: '100%', height: 18, borderRadius: 2, overflow: 'hidden', gap: '1px', bgcolor: 'grey.200' }}>
      {catConfig.map(({ key, color }) => {
        const cat = categories.find(c => c.key === key);
        const bytes = cat?.bytes || 0;
        const width = pct(bytes, total);
        if (width === 0) return null;
        return (
          <Tooltip key={key} title={`${cat?.label || key}: ${formatBytes(bytes)}`} arrow>
            <Box sx={{ width: `${width}%`, bgcolor: color, transition: 'width 0.4s ease', minWidth: 2 }} />
          </Tooltip>
        );
      })}
    </Box>
  );
}

function CategoryRow({ label, bytes, total, color, Icon }) {
  const fraction = total > 0 ? bytes / total : 0;
  const pctLabel = total > 0 && bytes > 0 ? `${(fraction * 100).toFixed(fraction < 0.01 ? 1 : 0)}%` : null;
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: color, flexShrink: 0 }} />
      <Icon sx={{ fontSize: 15, color: 'text.secondary', flexShrink: 0 }} />
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{label}</Typography>
      {pctLabel && (
        <Typography variant="caption" color="text.disabled">{pctLabel}</Typography>
      )}
      <Typography variant="body2" fontWeight={500} color="text.primary" sx={{ minWidth: 56, textAlign: 'right' }}>
        {formatBytes(bytes)}
      </Typography>
    </Stack>
  );
}

function PhotosDetail({ detail }) {
  if (!detail) return null;
  const items = [
    { label: 'Original photos', bytes: detail.originals || 0 },
    { label: 'Preview images',  bytes: detail.previews  || 0 },
    { label: 'Thumbnails',      bytes: detail.thumbnails || 0 },
    { label: 'Search indexes',  bytes: detail.staging   || 0 },
    { label: 'Gallery data',    bytes: detail.gallery_data || 0 },
  ].filter(i => i.bytes > 0);

  if (!items.length) return null;
  const subTotal = items.reduce((s, i) => s + i.bytes, 0);
  return (
    <Box sx={{ pl: 2.5, mt: 0.5, borderLeft: '2px solid', borderColor: 'grey.200' }}>
      {items.map(({ label, bytes }) => (
        <Stack key={label} direction="row" justifyContent="space-between" sx={{ py: 0.25 }}>
          <Typography variant="caption" color="text.disabled">{label}</Typography>
          <Typography variant="caption" color="text.secondary">{formatBytes(bytes)}</Typography>
        </Stack>
      ))}
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const StorageProfiler = ({ profileApi }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPhotoDetail, setShowPhotoDetail] = useState(false);

  const compute = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await profileApi.getStorage(true);
      setData(result);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to compute storage');
    } finally {
      setLoading(false);
    }
  };

  const s3 = data?.s3 || {};
  const db = data?.db || {};
  const s3Total = s3.total_bytes || 0;
  const dbTotal = db.total_bytes || 0;
  const grandTotal = s3Total + dbTotal;
  const s3Cats = s3.categories || [];
  const dbCats = db.categories || [];

  const photosCat = s3Cats.find(c => c.key === 'photos');

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      {/* Header */}
      <Stack direction="row" spacing={1.5} alignItems="center" mb={data ? 2 : 1.5}>
        <StorageIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>Storage Used</Typography>
          <Typography variant="caption" color="text.secondary">
            Cloud storage &amp; database space used by your account
          </Typography>
        </Box>
        {data && (
          <Button
            size="small"
            variant="outlined"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
            onClick={compute}
            disabled={loading}
          >
            Refresh
          </Button>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Results */}
      {data && !loading && (
        <Box>
          {/* Grand total */}
          <Stack direction="row" alignItems="baseline" spacing={1} mb={0.5}>
            <Typography variant="h5" fontWeight={700}>{formatBytes(grandTotal)}</Typography>
            <Typography variant="body2" color="text.secondary">total used</Typography>
          </Stack>

          {/* S3 vs DB split bar */}
          <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', width: '100%', height: 10, borderRadius: 2, overflow: 'hidden', gap: '2px', bgcolor: 'grey.200', mb: 0.75 }}>
              <Tooltip title={`Cloud Storage: ${formatBytes(s3Total)}`} arrow>
                <Box sx={{ width: `${pct(s3Total, grandTotal)}%`, bgcolor: '#1565C0', transition: 'width 0.4s', minWidth: s3Total > 0 ? 2 : 0 }} />
              </Tooltip>
              <Tooltip title={`Database: ${formatBytes(dbTotal)}`} arrow>
                <Box sx={{ width: `${pct(dbTotal, grandTotal)}%`, bgcolor: '#283593', transition: 'width 0.4s', minWidth: dbTotal > 0 ? 2 : 0 }} />
              </Tooltip>
            </Box>
            <Stack direction="row" spacing={2}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <CloudIcon sx={{ fontSize: 14, color: '#1565C0' }} />
                <Typography variant="caption" color="text.secondary">Cloud: <b>{formatBytes(s3Total)}</b></Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <DatasetIcon sx={{ fontSize: 14, color: '#283593' }} />
                <Typography variant="caption" color="text.secondary">Database: <b>{formatBytes(dbTotal)}</b></Typography>
              </Stack>
            </Stack>
          </Box>

          {/* Cloud Storage breakdown */}
          <Box mb={2.5}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <CloudIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" fontWeight={600}>Cloud Storage</Typography>
              <Typography variant="caption" color="text.disabled">({formatBytes(s3Total)})</Typography>
            </Stack>

            <StackedBar categories={s3Cats} total={s3Total} catConfig={S3_CATS} />

            <Stack spacing={1.25} mt={1.5}>
              {S3_CATS.map(({ key, label, color, Icon }) => {
                const cat = s3Cats.find(c => c.key === key);
                const bytes = cat?.bytes || 0;
                return (
                  <Box key={key}>
                    <CategoryRow label={label} bytes={bytes} total={s3Total} color={color} Icon={Icon} />
                    {key === 'photos' && bytes > 0 && (
                      <Box>
                        <Typography
                          variant="caption"
                          color="primary"
                          sx={{ cursor: 'pointer', mt: 0.5, display: 'inline-block' }}
                          onClick={() => setShowPhotoDetail(v => !v)}
                        >
                          {showPhotoDetail ? 'Hide breakdown' : 'Show breakdown'}
                        </Typography>
                        {showPhotoDetail && <PhotosDetail detail={photosCat?.detail} />}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>

          {/* Database breakdown */}
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <DatasetIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" fontWeight={600}>Database Records</Typography>
              <Typography variant="caption" color="text.disabled">({formatBytes(dbTotal)})</Typography>
              <Tooltip title="Estimated from average row sizes. Actual stored size may differ." placement="top">
                <Typography variant="caption" color="text.disabled" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>~est</Typography>
              </Tooltip>
            </Stack>

            <StackedBar categories={dbCats} total={dbTotal} catConfig={DB_CATS} />

            <Stack spacing={1.25} mt={1.5}>
              {DB_CATS.map(({ key, label, color, Icon }) => {
                const cat = dbCats.find(c => c.key === key);
                const bytes = cat?.bytes || 0;
                return (
                  <CategoryRow key={key} label={label} bytes={bytes} total={dbTotal} color={color} Icon={Icon} />
                );
              })}
            </Stack>
          </Box>

          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2, textAlign: 'right' }}>
            Last computed: {data.computed_at ? new Date(data.computed_at + 'Z').toLocaleString() : '-'}
            {data.cached && ' (cached)'}
          </Typography>
        </Box>
      )}

      {/* Compute button shown when no data */}
      {!data && (
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <StorageIcon />}
          onClick={compute}
          disabled={loading}
        >
          {loading ? 'Computing…' : 'Compute Storage Used'}
        </Button>
      )}
      {loading && data && (
        <Stack direction="row" spacing={1} alignItems="center" mt={1.5}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">Scanning storage, this may take a moment…</Typography>
        </Stack>
      )}
    </Paper>
  );
};

export default StorageProfiler;
